/**
 * Home hero session chart.
 * Binance-style candles; last 20 reveal one by one with a forming animation.
 */
(function () {
  'use strict';

  var HOURS = ['08:00', '09:00', '10:00', '11:00', '12:00', '01:00'];
  var PRICE_MIN = 25800;
  var PRICE_MAX = 26600;
  var ANIM_COUNT = 20;
  var FORM_MS = 700;
  var GAP_MS = 100;
  var PAUSE_MS = 2800;
  var UP_COLOR = '#2f9e44';
  var DOWN_COLOR = '#F6465D';
  var BG_COLOR = '#0B0E11';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function seeded(i) {
    var x = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
    return x - Math.floor(x);
  }

  function lerp(a, b, x) {
    return a + (b - a) * x;
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  /** Simulate live Binance ticks while a candle is forming. */
  function formingOHLC(c, candleIdx, t) {
    var steps = 36;
    var stepCount = Math.max(1, Math.floor(easeInOutQuad(t) * steps));
    var open = c.open;
    var close = open;
    var high = open;
    var low = open;
    var s;
    var st;
    var target;
    var noise;

    for (s = 1; s <= stepCount; s += 1) {
      st = s / steps;
      target = lerp(open, c.close, st);
      noise = (seeded(candleIdx * 31 + s) - 0.5) * 12 * (1 - st * 0.6);
      close = target + noise;
      high = Math.max(high, close);
      low = Math.min(low, close);
    }

    if (t >= 1) {
      return { open: c.open, close: c.close, high: c.high, low: c.low };
    }

    return { open: open, close: close, high: high, low: low };
  }

  /**
   * Client screenshot pattern (matched carefully): high open on the left,
   * drift down into a sideways consolidation, volatile spike cluster
   * (red box anchors there), sharp breakdown, stair-step downtrend to the
   * low, then a strong recovery rally on the right with a small dip at
   * the very end.
   */
  function priceAt(t) {
    var points = [
      { t: 0.0, p: 26490 },
      { t: 0.05, p: 26380 },
      { t: 0.12, p: 26300 },
      { t: 0.2, p: 26260 },
      { t: 0.26, p: 26410 },
      { t: 0.3, p: 26320 },
      { t: 0.34, p: 26380 },
      { t: 0.41, p: 26220 },
      { t: 0.48, p: 26050 },
      { t: 0.54, p: 26110 },
      { t: 0.62, p: 25970 },
      { t: 0.68, p: 26020 },
      { t: 0.76, p: 25880 },
      { t: 0.82, p: 25830 },
      { t: 0.9, p: 25970 },
      { t: 0.96, p: 26130 },
      { t: 1.0, p: 26080 },
    ];
    var i;

    for (i = 0; i < points.length - 1; i += 1) {
      if (t <= points[i + 1].t) {
        var local = (t - points[i].t) / (points[i + 1].t - points[i].t);
        return lerp(points[i].p, points[i + 1].p, local);
      }
    }

    return points[points.length - 1].p;
  }

  /**
   * Realistic random walk around the session skeleton: candles overlap,
   * bodies vary, trends include counter-trend pullback candles, wicks
   * scale with volatility. Looks like a real exchange chart, but the
   * overall zig-zag path and the final rally are preserved.
   */
  function buildSeries() {
    var n = 52;
    var candles = [];
    var i;
    var t;
    var trend;
    var open;
    var close;
    var drift;
    var noise;
    var vol;
    var bodyRange;
    var upWick;
    var downWick;

    for (i = 0; i < n; i += 1) {
      t = i / (n - 1);
      trend = priceAt(t);
      open = i === 0 ? 26485 : candles[i - 1].close;

      drift = (trend - open) * 0.7;
      noise = (seeded(i * 7.31 + 2.7) - 0.5) * 18;
      if (seeded(i * 4.77) > 0.85) noise *= 1.8;
      close = open + drift + noise;

      bodyRange = Math.abs(close - open);
      vol = 5 + seeded(i * 1.93) * 9;
      upWick = vol * (0.5 + seeded(i * 3.17) * 0.9) + bodyRange * 0.2;
      downWick = vol * (0.5 + seeded(i * 5.23) * 0.9) + bodyRange * 0.2;

      candles.push({
        open: open,
        high: Math.max(open, close) + upWick,
        low: Math.min(open, close) - downWick,
        close: close,
      });
    }
    return candles;
  }

  /**
   * Red box starts at the first small red candle after the rally peak —
   * the candle marked with the green arrow in the client's screenshot.
   */
  function findBoxCandle(candles) {
    var n = candles.length;
    var searchStart = Math.round(n * 0.22);
    var searchEnd = Math.min(n - 1, Math.round(n * 0.42));
    var peakIdx = searchStart;
    var i;

    for (i = searchStart + 1; i <= searchEnd; i += 1) {
      if (candles[i].high > candles[peakIdx].high) peakIdx = i;
    }

    for (i = peakIdx; i <= searchEnd; i += 1) {
      if (candles[i].close < candles[i].open) return i;
    }

    return peakIdx;
  }

  function initChart(canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var candles = buildSeries();
    var n = candles.length;
    var animStart = Math.max(0, n - ANIM_COUNT);
    var boxCandleIdx = findBoxCandle(candles);
    var boxCandle = candles[boxCandleIdx];
    var visibleAnimated = prefersReducedMotion() ? n - animStart : 0;
    var formingProgress = prefersReducedMotion() ? 1 : 0;
    var formingStart = 0;
    var gapStart = 0;
    var phase = 'forming';
    var pauseUntil = 0;
    var raf;

    function resize() {
      var rect = canvas.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function yAt(price, top, chartH) {
      return top + ((PRICE_MAX - price) / (PRICE_MAX - PRICE_MIN)) * chartH;
    }

    function xAt(i, padL, gap) {
      return padL + gap * i + gap / 2;
    }

    function snap(value) {
      return Math.round(value) + 0.5;
    }

    function candleIndexVisible(i) {
      if (i < animStart) return true;
      if (i < animStart + visibleAnimated) return true;
      return i === animStart + visibleAnimated && formingProgress > 0;
    }

    function drawBinanceCandle(i, padL, gap, bodyW, padT, chartH, progress) {
      if (!candleIndexVisible(i)) return;

      var c = candles[i];
      var forming = i === animStart + visibleAnimated && progress < 1;
      var ohlc = forming ? formingOHLC(c, i, progress) : c;
      var open = ohlc.open;
      var close = ohlc.close;
      var high = ohlc.high;
      var low = ohlc.low;
      var up = close >= open;
      var color = up ? UP_COLOR : DOWN_COLOR;
      var x = snap(xAt(i, padL, gap));
      var yHigh = snap(yAt(high, padT, chartH));
      var yLow = snap(yAt(low, padT, chartH));
      var yOpen = yAt(open, padT, chartH);
      var yClose = yAt(close, padT, chartH);
      var bodyTop = snap(Math.min(yOpen, yClose));
      var bodyBottom = snap(Math.max(yOpen, yClose));
      var bodyH = Math.max(1, bodyBottom - bodyTop);
      var left = snap(x - bodyW / 2);

      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';

      ctx.beginPath();
      ctx.moveTo(x, yHigh);
      ctx.lineTo(x, yLow);
      ctx.stroke();

      if (bodyH <= 1.2) {
        ctx.beginPath();
        ctx.moveTo(left, bodyTop);
        ctx.lineTo(left + bodyW, bodyTop);
        ctx.stroke();
      } else {
        ctx.fillRect(left, bodyTop, bodyW, bodyH);
      }
    }

    function draw() {
      var w = canvas.getBoundingClientRect().width;
      var h = canvas.getBoundingClientRect().height;
      var padL = 14;
      var padR = 58;
      var padT = 18;
      var padB = 28;
      var chartH = h - padT - padB;
      var plotW = w - padL - padR;
      var gap = plotW / n;
      var bodyW = Math.max(2.5, gap * 0.55);
      var i;
      var gy;
      var priceLabel;
      var boxX;
      var boxY;
      var boxH;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = BG_COLOR;
      ctx.fillRect(0, 0, w, h);

      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.font = '500 10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.textAlign = 'right';
      for (i = 0; i <= 4; i += 1) {
        priceLabel = PRICE_MAX - ((PRICE_MAX - PRICE_MIN) * i) / 4;
        gy = padT + (chartH / 4) * i;
        ctx.beginPath();
        ctx.moveTo(padL, snap(gy));
        ctx.lineTo(w - padR, snap(gy));
        ctx.stroke();
        ctx.fillText(Math.round(priceLabel).toLocaleString('en-US'), w - 8, gy + 3);
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      for (i = 0; i < HOURS.length; i += 1) {
        var fi = Math.round((i / (HOURS.length - 1)) * (n - 1));
        ctx.fillText(HOURS[i], xAt(fi, padL, gap), h - 8);
      }

      boxX = padL + gap * boxCandleIdx;
      boxY = yAt(boxCandle.high, padT, chartH);
      boxH = yAt(boxCandle.low, padT, chartH) - boxY;
      ctx.fillStyle = 'rgba(246,70,93,0.28)';
      ctx.fillRect(boxX, boxY, w - padR - boxX, boxH);

      for (i = 0; i < n; i += 1) {
        drawBinanceCandle(i, padL, gap, bodyW, padT, chartH, formingProgress);
      }
    }

    function tick(ts) {
      if (!prefersReducedMotion()) {
        if (pauseUntil) {
          if (ts < pauseUntil) {
            raf = window.requestAnimationFrame(tick);
            return;
          }
          visibleAnimated = 0;
          formingProgress = 0;
          formingStart = 0;
          gapStart = 0;
          phase = 'forming';
          pauseUntil = 0;
        }

        if (visibleAnimated < n - animStart) {
          if (phase === 'forming') {
            if (!formingStart) formingStart = ts;
            formingProgress = Math.min(1, (ts - formingStart) / FORM_MS);
            draw();

            if (formingProgress >= 1) {
              formingProgress = 1;
              phase = 'gap';
              gapStart = ts;
              draw();
            }
          } else if (phase === 'gap') {
            draw();
            if (ts - gapStart >= GAP_MS) {
              visibleAnimated += 1;
              formingProgress = 0;
              formingStart = 0;
              phase = 'forming';
            }
          }
        } else if (!pauseUntil) {
          draw();
          pauseUntil = ts + PAUSE_MS;
        }
      }

      raf = window.requestAnimationFrame(tick);
    }

    resize();
    draw();
    window.addEventListener('resize', function () {
      resize();
      draw();
    });

    if (!prefersReducedMotion()) {
      raf = window.requestAnimationFrame(tick);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && raf) {
        window.cancelAnimationFrame(raf);
        raf = null;
      } else if (!document.hidden && !raf && !prefersReducedMotion()) {
        formingStart = 0;
        gapStart = 0;
        phase = 'forming';
        raf = window.requestAnimationFrame(tick);
      }
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    document.querySelectorAll('[data-hero-chart]').forEach(initChart);
  });
})();
